import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { NativeTracking, type TrackingStatus, type TrackingStatusValue } from "./plugins";

function isNativeIOS(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}

const UNAVAILABLE: TrackingStatus = { status: "unavailable" };

export async function getTrackingStatus(): Promise<TrackingStatus> {
  if (!isNativeIOS()) return UNAVAILABLE;
  try {
    return await NativeTracking.getStatus();
  } catch (error) {
    console.error("NativeTracking.getStatus failed", error);
    return UNAVAILABLE;
  }
}

/** Presents the native ATT prompt. iOS only fires it once (when notDetermined). */
export async function requestTracking(): Promise<TrackingStatus> {
  if (!isNativeIOS()) return UNAVAILABLE;
  try {
    return await NativeTracking.requestPermission();
  } catch (error) {
    console.error("NativeTracking.requestPermission failed", error);
    return UNAVAILABLE;
  }
}

/** True only when the ATT prompt can still be shown (status is notDetermined). */
export function canRequestTracking(status: TrackingStatusValue | null): boolean {
  return Capacitor.getPlatform() === "ios" && status === "notDetermined";
}

type TrackingContextValue = {
  status: TrackingStatusValue | null;
  statusReady: boolean;
  refreshStatus: () => Promise<TrackingStatus>;
  requestPermission: () => Promise<TrackingStatus>;
};

const TrackingContext = createContext<TrackingContextValue | null>(null);

export function TrackingProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<TrackingStatusValue | null>(null);
  const [statusReady, setStatusReady] = useState(Capacitor.getPlatform() !== "ios");

  const refreshStatus = useCallback(async () => {
    const next = await getTrackingStatus();
    setStatus(next.status);
    setStatusReady(true);
    return next;
  }, []);

  const requestPermission = useCallback(async () => {
    const next = await requestTracking();
    setStatus(next.status);
    setStatusReady(true);
    return next;
  }, []);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const listener = App.addListener("appStateChange", (event) => {
      if (event.isActive) void refreshStatus();
    });
    return () => {
      void listener.then((handle) => handle.remove());
    };
  }, [refreshStatus]);

  const value = useMemo(
    () => ({ status, statusReady, refreshStatus, requestPermission }),
    [status, statusReady, refreshStatus, requestPermission],
  );

  return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>;
}

export function useTracking() {
  const context = useContext(TrackingContext);
  if (!context) {
    throw new Error("useTracking must be used within TrackingProvider");
  }
  return context;
}
